// @flow

import React from "react";
import { connect } from "react-redux";
import { compose } from "redux";
import { localize } from "redux-i18n";
import { push } from "connected-react-router";

import * as routes from "@routes/constants";
import * as accountSelectors from "@modules/account/selectors";
import actionCreators from "@modules/context/actionCreator";
import * as contextSelectors from "@modules/context";
import type { State } from "@store/reducers";
import type { Client } from "@modules/context/types";
import "./client-context.scss";
import sharedActionCreators from "@modules/shared/actionCreator";
import employeeActionCreators from "@modules/employee/actionCreator";

type OwnProps = $Exact<{
  currentPath: string,
}>;

type PropsFromState = $Exact<{
  client: Client | null,
  hasContextSwitch: boolean,
  hasReportingContextSwitch: boolean,
}>;

type PropsFromDispatch = $Exact<{
  redirect: string => () => void,
  clearContext: () => typeof actionCreators.ClearClientContext,
  resetActiveHierarchyTree: typeof sharedActionCreators.ResetMyHierarchyTree,
  clearEmployeeClientList: typeof employeeActionCreators.SetEmployeesDefaultData,
}>;

type Props = $Exact<{
  ...OwnProps,
  ...PropsFromState,
  ...PropsFromDispatch,
  ...PropsFromLocalize,
}>;

export class ClientContext extends React.Component<Props> {
  get showClientContext() {
    const { hasContextSwitch, client, currentPath, hasReportingContextSwitch } = this.props;

    return (
      hasContextSwitch &&
      client &&
      currentPath.startsWith(routes.ADMINISTRATION_CLIENT_DETAILS_PATH)
    );
  }

  get clientContextRedirect() {
    if (this.props.currentPath.startsWith(routes.ADMINISTRATION_CLIENT_DETAILS_PATH)) {
      return routes.ADMINISTRATION_CLIENT_DETAILS_PATH;
    }

    return routes.HOME_PATH;
  }

  handleClick = () => {
    this.props.redirect(this.clientContextRedirect);
    this.props.clearContext();
    this.props.resetActiveHierarchyTree();
    this.props.clearEmployeeClientList();
  };

  render() {
    if (!this.showClientContext) {
      return null;
    }

    return (
      <div className="d-flex align-items-center justify-content-start">
        {this.props.client && (
          <div className="client-name">{this.props.client ? this.props.client.name : null}</div>
        )}
        <div className="change-button" onClick={this.handleClick}>
          {this.props.t("CHANGE")}
        </div>
      </div>
    );
  }
}

const mapStateToProps = (state: State): PropsFromState => ({
  client: contextSelectors.getClientContext(state),
  hasContextSwitch: accountSelectors.hasClientContextSwitch(state),
  hasReportingContextSwitch: accountSelectors.hasReportingClientContextSwitch(state),
});

const mapDispatchToProps = (dispatch: Function) => ({
  redirect: path => dispatch(push(path)),
  clearContext: () => dispatch(actionCreators.ClearClientContext()),
  resetActiveHierarchyTree: () => dispatch(sharedActionCreators.ResetMyHierarchyTree()),
  clearEmployeeClientList: () => dispatch(employeeActionCreators.SetEmployeesDefaultData()),
});

export default compose(localize(), connect(mapStateToProps, mapDispatchToProps))(ClientContext);
